/**
 * Subscription helpers shared by the banners, Org Account, the suspended page and the
 * admit / add-patient flows (Phase 13). Pure functions only; no PHI.
 */

import type { AccountSubscription } from '../types/permissions';
import type { PatientCapReachedDetails, PaymentMethodDto, PlanId, SubscriptionStatus } from '../dtos/billing/subscription.dto';
import { DEFAULT_AGENCY_TIMEZONE } from '../types/permissions';

export const PLAN_IDS: readonly PlanId[] = ['starter', 'core', 'advanced', 'custom'];
export const SUBSCRIPTION_STATUSES: readonly SubscriptionStatus[] = [
  'trialing',
  'active',
  'past_due',
  'read_only',
  'suspended',
  'canceled',
];

const PLAN_LABELS: Record<PlanId, string> = {
  starter: 'Starter',
  core: 'Core',
  advanced: 'Advanced',
  custom: 'Custom',
};

/** Tooltip on payment controls for a tenant whose `subscriptionBilling` flag is off. */
export const BILLING_DISABLED_TITLE = 'Billing not enabled in demo';

/** Sales contact for the "Let's talk" (custom plan) card. */
export const SALES_EMAIL = 'sales@pelorahealth.com';

/** First trial day on which an upgrade prompt may appear (ACC-08: no card prompt before day 10 of 14). */
export const TRIAL_UPGRADE_PROMPT_DAYS_LEFT = 4;

/** Cap banner threshold (percent of the patient cap in use). */
export const CAP_WARNING_PCT = 80;

/** "Starter" for 'starter'; the raw value capitalised for anything unknown. */
export function planLabel(plan: string | null | undefined): string {
  if (!plan) return '';
  const key = plan.toLowerCase() as PlanId;
  if (PLAN_LABELS[key]) return PLAN_LABELS[key];
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

/** $49.00 → "$49"; $19.99 → "$19.99". */
export function formatCents(cents: number | null | undefined): string {
  const value = Number(cents) || 0;
  const dollars = value / 100;
  return dollars % 1 === 0
    ? `$${dollars.toLocaleString('en-US')}`
    : `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Always two decimals, for invoices: "$199.00". */
export function formatCentsExact(cents: number | null | undefined): string {
  const value = (Number(cents) || 0) / 100;
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** "October 31, 2026" in the agency timezone; null for a missing or invalid date. */
export function formatAccountDate(iso: string | null | undefined, timeZone: string = DEFAULT_AGENCY_TIMEZONE): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
  try {
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone }).format(date);
  } catch {
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone: DEFAULT_AGENCY_TIMEZONE }).format(date);
  }
}

/** Short date for tables: "Oct 31, 2026". */
export function formatShortDate(iso: string | null | undefined, timeZone: string = DEFAULT_AGENCY_TIMEZONE): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
  try {
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone }).format(date);
  } catch {
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone: DEFAULT_AGENCY_TIMEZONE }).format(date);
  }
}

/** Percent of the cap in use (0–100, may exceed 100); null without a usable limit. */
export function capPercent(used: number, limit: number | null): number | null {
  if (limit === null || !Number.isFinite(limit) || limit <= 0) return null;
  return Math.round((used / limit) * 100);
}

const toNumberOrNull = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;
const toStringOrNull = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);

/**
 * Normalize the `subscription` block of GET /users/me/permissions. Returns undefined when it is
 * missing or malformed (older backend): the UI then shows no subscription banners and never blocks.
 */
export function normalizeSubscription(value: unknown): AccountSubscription | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = value as Record<string, unknown>;
  const status = typeof raw.status === 'string' ? (raw.status.toLowerCase() as SubscriptionStatus) : undefined;
  if (!status || !SUBSCRIPTION_STATUSES.includes(status)) return undefined;
  const planRaw = typeof raw.plan === 'string' ? (raw.plan.toLowerCase() as PlanId) : undefined;
  const plan: PlanId = planRaw && PLAN_IDS.includes(planRaw) ? planRaw : 'core';
  const capRaw = raw.cap && typeof raw.cap === 'object' ? (raw.cap as Record<string, unknown>) : {};
  const used = toNumberOrNull(capRaw.used) ?? 0;
  const limit = toNumberOrNull(capRaw.limit);
  return {
    plan,
    status,
    trialDaysLeft: toNumberOrNull(raw.trialDaysLeft),
    trialEndsAt: toStringOrNull(raw.trialEndsAt),
    currentPeriodEnd: toStringOrNull(raw.currentPeriodEnd),
    cancelAtPeriodEnd: raw.cancelAtPeriodEnd === true,
    exportUntil: toStringOrNull(raw.exportUntil),
    // Missing flag → treat as demo (payment controls disabled) rather than send users to a 409
    billingEnabled: raw.billingEnabled === true,
    hasPaymentMethod: raw.hasPaymentMethod === true,
    cap: { used, limit, pct: capPercent(used, limit) ?? toNumberOrNull(capRaw.pct) },
  };
}

/** Suspended and canceled accounts are export-only: staff routes go to /account/suspended. */
export function isAccountBlocked(subscription: Pick<AccountSubscription, 'status'> | null | undefined): boolean {
  return subscription?.status === 'suspended' || subscription?.status === 'canceled';
}

/**
 * What choosing a plan does (plan §3.3), from the status, whether a card is on file (a Stripe
 * subscription) and whether a cancellation is scheduled. The server is the authority.
 *   checkout  Stripe Checkout: no card after the trial (read-only, suspended, comp), or canceled
 *   change    POST /subscription/change: a trial switches at once (card or not); a paid plan
 *             upgrades now (prorated) or downgrades at the period end
 *   none      past due, a declined card (read-only / suspended), or a scheduled cancellation:
 *             no plan buttons; planActionReason() says what to do instead
 */
export type PlanAction = 'checkout' | 'change' | 'none';

export function planAction(status: SubscriptionStatus, hasPaymentMethod: boolean, cancelAtPeriodEnd = false): PlanAction {
  if (cancelAtPeriodEnd && (status === 'trialing' || status === 'active' || status === 'past_due')) return 'none';
  switch (status) {
    case 'past_due':
      return 'none';
    case 'canceled':
      return 'checkout';
    case 'trialing':
      return 'change';
    case 'active':
      return hasPaymentMethod ? 'change' : 'checkout';
    case 'read_only':
    case 'suspended':
      return hasPaymentMethod ? 'none' : 'checkout';
    default:
      return 'none';
  }
}

/** Why there are no plan buttons (planAction 'none'); null when plans can be chosen. */
export function planActionReason(status: SubscriptionStatus, hasPaymentMethod: boolean, cancelAtPeriodEnd = false): string | null {
  if (planAction(status, hasPaymentMethod, cancelAtPeriodEnd) !== 'none') return null;
  if (cancelAtPeriodEnd && (status === 'trialing' || status === 'active' || status === 'past_due')) {
    return 'Undo the cancellation to change plan.';
  }
  return 'Update your card to change plan.';
}

/** A trial plan switch without a card never reaches Stripe, so it works in a demo account too. */
export function planActionNeedsBilling(status: SubscriptionStatus, hasPaymentMethod: boolean): boolean {
  return !(status === 'trialing' && !hasPaymentMethod);
}

/** "Visa ending 4242 · expires 08/28" (display data only; no card number ever reaches the SPA). */
export function cardSummary(card: PaymentMethodDto): string {
  const brand = card.brand ? card.brand.charAt(0).toUpperCase() + card.brand.slice(1) : 'Card';
  const month = String(card.expMonth).padStart(2, '0');
  const year = String(card.expYear % 100).padStart(2, '0');
  return `${brand} ending ${card.last4} · expires ${month}/${year}`;
}

/** True when the card stops working before the given date (a card is valid through the last day of its expiry month). */
export function cardExpiresBefore(card: PaymentMethodDto, iso: string | null | undefined): boolean {
  if (!iso || !card.expYear || !card.expMonth) return false;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return false;
  // Date.UTC months are 0-based, so expMonth is the first day of the month after expiry
  return Date.UTC(card.expYear, card.expMonth, 1) <= date.getTime();
}

/** "You've reached the 10-patient limit on Starter. Upgrade to admit more." */
export function capReachedMessage(details: Partial<PatientCapReachedDetails> | Record<string, unknown> | undefined): string {
  const limit = details && typeof details.limit === 'number' ? details.limit : null;
  const plan = details && typeof details.plan === 'string' ? planLabel(details.plan) : '';
  if (limit !== null && plan) {
    return `You've reached the ${limit}-patient limit on ${plan}. Upgrade to admit more.`;
  }
  return "You've reached the patient limit on your plan. Upgrade to admit more.";
}

const CHARGE_REASON_LABELS: Record<string, string> = {
  hipaa_audit_log_download: 'HIPAA audit log export',
  hipaa_audit_log_export: 'HIPAA audit log export',
  audit_log_export: 'HIPAA audit log export',
  audit_export: 'HIPAA audit log export',
};

/** Readable label for a pending charge reason ("hipaa_audit_log_download" → "HIPAA audit log export"). */
export function chargeReasonLabel(reason: string | null | undefined): string {
  if (!reason) return 'Charge';
  const known = CHARGE_REASON_LABELS[reason.toLowerCase()];
  if (known) return known;
  const words = reason.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Charge';
}

/** Canceled and past the post-cancel export window (backend answers 402 ACCOUNT_CLOSED). */
export function isAccountClosed(
  subscription: Pick<AccountSubscription, 'status' | 'exportUntil'> | null | undefined,
  now: Date = new Date()
): boolean {
  if (subscription?.status !== 'canceled' || !subscription.exportUntil) return false;
  const until = new Date(subscription.exportUntil);
  return !Number.isNaN(until.getTime()) && until.getTime() < now.getTime();
}
